import { useMemo } from "react";

import { SearchableSelect } from "../common/SearchableSelect";

export const BULK_ASSIGNMENT_KEEP_VALUE = "__keep_current__";

const getEquipmentSafetyLabel = (item, kind) => {
  const identifier = kind === "Truck" ? item.truck_number : item.trailer_number;
  if (!item?.safety_expiration_date) {
    return `${identifier} • safety missing`;
  }
  if (item.safety_expiration_date < new Date().toISOString().slice(0, 10)) {
    return `${identifier} • expired ${item.safety_expiration_date}`;
  }
  return null;
};

export const DispatchBoardBulkActions = ({ applying, hasPendingChanges, lookups, onApply, onClearSelection, onValueChange, selectedCount, values }) => {
  const driverOptions = useMemo(() => [
    { value: BULK_ASSIGNMENT_KEEP_VALUE, label: "Keep current driver", searchText: "keep current unchanged" },
    { value: "", label: "Clear driver", searchText: "clear driver unassigned" },
    ...lookups.drivers.map((driver) => ({ value: driver.id, label: driver.full_name, searchText: `${driver.full_name} ${driver.phone || ""}` })),
  ], [lookups.drivers]);

  const truckOptions = useMemo(() => [
    { value: BULK_ASSIGNMENT_KEEP_VALUE, label: "Keep current truck", searchText: "keep current unchanged" },
    { value: "", label: "Clear truck", searchText: "clear truck unassigned" },
    ...lookups.trucks.map((truck) => ({ value: truck.id, label: truck.truck_number, meta: getEquipmentSafetyLabel(truck, "Truck") || undefined, searchText: `${truck.truck_number} ${truck.safety_expiration_date || ""}` })),
  ], [lookups.trucks]);

  const trailerOptions = useMemo(() => [
    { value: BULK_ASSIGNMENT_KEEP_VALUE, label: "Keep current trailer", searchText: "keep current unchanged" },
    { value: "", label: "Clear trailer", searchText: "clear trailer unassigned" },
    ...lookups.trailers.map((trailer) => ({ value: trailer.id, label: trailer.trailer_number, meta: getEquipmentSafetyLabel(trailer, "Trailer") || undefined, searchText: `${trailer.trailer_number} ${trailer.safety_expiration_date || ""}` })),
  ], [lookups.trailers]);

  return (
    <section className="panel-surface flex flex-wrap items-end gap-3 px-5 py-4" data-testid="dispatch-board-bulk-actions-panel">
      <div className="min-w-[180px] space-y-1" data-testid="dispatch-board-bulk-actions-summary">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Bulk actions</p>
        <p className="text-sm font-semibold text-zinc-900" data-testid="dispatch-board-bulk-actions-selected-count">{selectedCount} trip{selectedCount === 1 ? "" : "s"} selected</p>
        <p className="text-xs text-zinc-500" data-testid="dispatch-board-bulk-actions-note">Statuses still update automatically from assignment and stop completion.</p>
      </div>

      <label className="min-w-[220px] flex-1 space-y-1">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Driver</span>
        <SearchableSelect dataTestId="dispatch-board-bulk-driver" onChange={(value) => onValueChange("driver_id", value)} options={driverOptions} placeholder="Keep current driver" searchPlaceholder="Search drivers..." triggerClassName="rounded-lg px-3 py-2" value={values.driver_id} />
      </label>

      <label className="min-w-[220px] flex-1 space-y-1">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Truck</span>
        <SearchableSelect dataTestId="dispatch-board-bulk-truck" onChange={(value) => onValueChange("truck_id", value)} options={truckOptions} placeholder="Keep current truck" searchPlaceholder="Search trucks..." triggerClassName="rounded-lg px-3 py-2" value={values.truck_id} />
      </label>

      <label className="min-w-[220px] flex-1 space-y-1">
        <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Trailer</span>
        <SearchableSelect dataTestId="dispatch-board-bulk-trailer" onChange={(value) => onValueChange("trailer_id", value)} options={trailerOptions} placeholder="Keep current trailer" searchPlaceholder="Search trailers..." triggerClassName="rounded-lg px-3 py-2" value={values.trailer_id} />
      </label>

      <button className="rounded-lg border border-zinc-300 bg-white px-4 py-2.5 text-sm font-semibold text-zinc-700 transition hover:border-zinc-900 hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-60" data-testid="dispatch-board-bulk-clear-selection" disabled={!selectedCount || applying} onClick={onClearSelection} type="button">Clear selection</button>
      <button className="rounded-lg bg-zinc-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60" data-testid="dispatch-board-bulk-apply" disabled={!selectedCount || !hasPendingChanges || applying} onClick={onApply} type="button">{applying ? "Applying..." : "Apply assignments"}</button>
    </section>
  );
};